import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from '../../../utils/i18n';
import { bankAPI } from '../../../utils/api';
import './StepStyles.css';

const Step7Notes = ({ formData, setFormData }) => {
  const { language } = useAuth();
  const { t } = useTranslation(language);
  const [banks, setBanks] = useState([]);
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    fetchBanks();
  }, []);

  useEffect(() => {
    if (formData.bankId) {
      fetchBranches(formData.bankId);
    }
  }, [formData.bankId]);

  const fetchBanks = async () => {
    try {
      const response = await bankAPI.getAll();
      setBanks(response.data.banks || []);
    } catch (error) {
      console.error('Failed to fetch banks:', error);
    }
  };

  const fetchBranches = async (bankId) => {
    try {
      const response = await bankAPI.getBranches(bankId);
      setBranches(response.data.branches || []);
    } catch (error) {
      console.error('Failed to fetch branches:', error);
    }
  };

  const handleNotesChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      notes: e.target.value,
    }));
  };

  // Format summary data
  const formatCurrency = (amount) => {
    return amount ? amount.toLocaleString('ko-KR') + ' KRW' : '0 KRW';
  };

  const getBankName = () => {
    if (!formData.bankId) return t('notSelected');
    const bank = banks.find(b => b.id === formData.bankId);
    return bank ? bank.name : `${t('bankLabel')} #${formData.bankId}`;
  };

  const getBranchName = () => {
    if (!formData.branchId) return t('notSelected');
    const branch = branches.find(b => b.id === formData.branchId);
    return branch ? branch.name : `${t('branchLabel')} #${formData.branchId}`;
  };

  return (
    <div className="step-container">
      <div className="summary-box">
        <h3 className="summary-title">📋 {t('loanSummary')}</h3>

        <div className="summary-section">
          <div className="summary-label">{t('customerLabel')}:</div>
          <div className="summary-value">{formData.customerName || t('notEntered')}</div>
        </div>

        <div className="summary-section">
          <div className="summary-label">{t('phoneLabel')}:</div>
          <div className="summary-value">{formData.customerPhone || t('notEntered')}</div>
        </div>

        <div className="summary-section">
          <div className="summary-label">{t('idLabel')}:</div>
          <div className="summary-value">{formData.customerIdNumber || t('notEntered')}</div>
        </div>

        <div className="summary-divider"></div>

        <div className="summary-section">
          <div className="summary-label">{t('bankLabel')}:</div>
          <div className="summary-value">{getBankName()}</div>
        </div>

        <div className="summary-section">
          <div className="summary-label">{t('branchLabel')}:</div>
          <div className="summary-value">{getBranchName()}</div>
        </div>

        <div className="summary-divider"></div>

        <div className="summary-section highlight">
          <div className="summary-label">{t('loanAmountLabel')}:</div>
          <div className="summary-value large">{formatCurrency(formData.amount)}</div>
        </div>

        <div className="summary-section">
          <div className="summary-label">{t('termLabel')}:</div>
          <div className="summary-value">{formData.termMonths || 0} {t('months')}</div>
        </div>

        <div className="summary-section">
          <div className="summary-label">{t('interestDateLabel')}:</div>
          <div className="summary-value">
            {t('dayOfMonth')} {formData.interestPaymentDate || '?'} {t('monthly')}
          </div>
        </div>

        <div className="summary-divider"></div>

        <div className="summary-section">
          <div className="summary-label">{t('documentsAttached')}:</div>
          <div className="summary-value">
            {formData.documents?.length || 0} {t('fileCount')}
          </div>
        </div>

        {formData.amount > 0 && formData.estimatedCommission && (
          <>
            <div className="summary-divider"></div>
            <div className="summary-section highlight success">
              <div className="summary-label">💰 {t('estimatedCommissionLabel')}:</div>
              <div className="summary-value large">
                {formatCurrency(formData.estimatedCommission)}
              </div>
              
            </div>
          </>
        )}
      </div>

      <div className="form-group">
        <label className="form-label">{t('notesOptional')}</label>
        <textarea
          value={formData.notes || ''}
          onChange={handleNotesChange}
          placeholder={t('notesPlaceholder')}
          rows="5"
        />
        <p className="form-hint">
          {t('notesExample')}
        </p>
      </div>

      <div className="step-info success">
        ✓ {t('loanComplete')}
      </div>
    </div>
  );
};

export default Step7Notes;
